/**
 * Which part of the deep prescription state is WRITTEN onto an order line.
 *
 * The wizard seeds DEFAULT_PRESCRIPTION_STATE.deep onto every line, insole
 * design branch included (top cover "EVA 2mm" / "DryFeet" / "Black", cup,
 * length), and buildLineConfiguration used to serialise the whole deep state
 * for every line type. A sandal never mounts the insole design section, so
 * sandal lines created since 2026-07-26 stored an insole top cover nobody
 * chose (27 of 139 sandal lines on 2026-09-17), and every surface that
 * summarised `deep.design` without asking what the line was printed it.
 *
 * The rule, in one place: a sandal line does not store `deep.design`.
 * Everything else a sandal uses is kept, in particular `deep.rearfoot`: the
 * sandal form renders the rearfoot section, and a missing rearfoot node
 * drops the whole rearfoot section from the lab sheet (see the comment on
 * DEFAULT_PRESCRIPTION_STATE).
 *
 * This is the WRITE side only. Rows that already carry the branch are not
 * rewritten by any migration; the display gates (isSandalLine.ts, migration
 * 20270359) hide it. The form STATE is deliberately left alone, so an
 * operator who picks sandal and then goes back to an insole subtype still
 * has the design defaults. The input is never mutated.
 *
 * Keyed on line_type because that is what the form state carries; the wizard
 * sets it to 'orthopedic_sandal' the moment the sandal subtype is picked.
 */
import type { PrescriptionDeepState } from './prescription/prescriptionState';

export function deepForLineType(
  lineType: string | null | undefined,
  deep: PrescriptionDeepState | undefined,
): PrescriptionDeepState | undefined {
  if (!deep) return deep;
  if (lineType !== 'orthopedic_sandal') return deep;
  const kept: PrescriptionDeepState = { ...deep };
  delete kept.design;
  return kept;
}
